import React, { useMemo, useState } from 'react';
import { Character } from '../types';
import { CHARACTERS } from '../constants';
import { Sparkles, Heart, HelpCircle } from 'lucide-react';

interface Props {
  onSelect: (p1: Character, p2: Character, firstSpeaker: 'A' | 'B') => void;
}

// ── Card back (face-down) ───────────────────────────────────────────
const CardBack: React.FC<{ label: string; gradient: string; hidden: boolean }> = ({ label, gradient, hidden }) => (
  <div className={`flip-card-face w-full h-full flex flex-col items-center justify-center ${gradient} card-back-pattern shadow-xl cursor-pointer select-none`}>
    {/* Only render animated content when not flipped — avoids backface bleed-through */}
    {!hidden && (
      <>
        <div className="absolute inset-0 animate-shimmer rounded-2xl pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30">
            <HelpCircle size={40} className="text-white/80" />
          </div>
          <span className="text-white font-bold text-lg tracking-widest">{label}</span>
          <span className="text-white/60 text-xs">点击揭晓</span>
        </div>
      </>
    )}
  </div>
);

// ── Card front (face-up, character info) ────────────────────────────
const CardFront: React.FC<{ char: Character }> = ({ char }) => (
  <div className="flip-card-face flip-card-front w-full h-full flex flex-col items-center justify-center bg-white shadow-xl p-5 gap-3">
    <img
      src={char.avatar}
      alt={char.name}
      className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
    />
    <h3 className="font-black text-xl text-[#2D2D2D]">
      {char.name}
      <span className="text-xs font-normal text-[#9CA3AF] ml-1">{char.age}岁</span>
    </h3>
    <p className="text-sm text-[#6B7280] font-medium">{char.job}</p>
    <span className="text-xs text-lavender-500 bg-lavender-100 px-3 py-1 rounded-full">
      {char.zodiac}
    </span>
    <p className="text-xs text-[#9CA3AF] text-center leading-relaxed line-clamp-2 max-w-[180px]">
      {char.bio}
    </p>
    <div className="flex flex-wrap justify-center gap-1 mt-1">
      {char.tags.slice(0, 3).map(t => (
        <span
          key={t}
          className="text-xs bg-cream text-[#6B7280] px-2 py-0.5 rounded-full border border-[#E5E0D8]"
        >
          #{t}
        </span>
      ))}
    </div>
  </div>
);

// ── Flip card container ─────────────────────────────────────────────
const FlipCard: React.FC<{
  character: Character;
  revealed: boolean;
  onReveal: () => void;
  label: string;
  gradient: string;
}> = ({ character, revealed, onReveal, label, gradient }) => (
  <div
    className={`flip-card w-[160px] h-[280px] sm:w-[200px] sm:h-[340px] ${revealed ? 'animate-card-bounce' : ''}`}
    onClick={() => !revealed && onReveal()}
  >
    <div className={`flip-card-inner ${revealed ? 'flipped' : ''}`}>
      <CardBack label={label} gradient={gradient} hidden={revealed} />
      <CardFront char={character} />
    </div>
  </div>
);

// ── Main component ──────────────────────────────────────────────────
const CharacterSelect: React.FC<Props> = ({ onSelect }) => {
  // Randomly pick one male and one female on mount (stable across re-renders)
  const { male, female } = useMemo(() => {
    const males = CHARACTERS.filter(c => c.gender === 'male');
    const females = CHARACTERS.filter(c => c.gender === 'female');
    return {
      male: males[Math.floor(Math.random() * males.length)],
      female: females[Math.floor(Math.random() * females.length)],
    };
  }, []);

  const [maleRevealed, setMaleRevealed] = useState(false);
  const [femaleRevealed, setFemaleRevealed] = useState(false);

  const bothRevealed = maleRevealed && femaleRevealed;

  const handleStart = () => {
    // Randomly decide who speaks first
    const firstSpeaker: 'A' | 'B' = Math.random() < 0.5 ? 'A' : 'B';
    onSelect(male, female, firstSpeaker);
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-cream to-coral-100 overflow-hidden">
      {/* Title */}
      <h1 className="text-2xl md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-coral-500 to-amber-accent text-center mb-3 flex items-center justify-center gap-2">
        <Sparkles size={20} className="text-amber-accent" />
        今日嘉宾
        <Sparkles size={20} className="text-amber-accent" />
      </h1>

      <p className="text-[#9CA3AF] text-sm mb-8">点击卡牌揭晓嘉宾身份</p>

      {/* Two cards side by side */}
      <div className="flex gap-5 md:gap-8 items-center">
        <FlipCard
          character={male}
          revealed={maleRevealed}
          onReveal={() => setMaleRevealed(true)}
          label="男嘉宾"
          gradient="bg-gradient-to-br from-sky-accent to-blue-600"
        />
        <FlipCard
          character={female}
          revealed={femaleRevealed}
          onReveal={() => setFemaleRevealed(true)}
          label="女嘉宾"
          gradient="bg-gradient-to-br from-coral-500 to-pink-600"
        />
      </div>

      {/* Start button area */}
      <div className="mt-10 h-16 flex items-center justify-center">
        {bothRevealed ? (
          <button
            onClick={handleStart}
            className="px-8 py-4 rounded-full bg-gradient-to-r from-coral-500 to-amber-accent hover:from-coral-700 hover:to-coral-500 text-white font-bold text-xl tracking-wider shadow-lg transition-all hover:shadow-xl flex items-center justify-center gap-2 animate-card-bounce"
          >
            <Heart size={20} />
            开始约会！
          </button>
        ) : (
          <p className="text-[#9CA3AF] text-sm animate-pulse">
            翻开卡牌揭晓今日嘉宾...
          </p>
        )}
      </div>
    </div>
  );
};

export default CharacterSelect;
